import SceneService from "../scene/scene_service";
import RenderService from "../render/render_service";
import ShaderService from "../shader/shader_service";
import ComputeService from "../compute/compute_service";
import CaptureService from "../capture/capture_service";
import InputService from "../input/inputservice";
import ResourceService from "../resource/resourceservice";
import CollisionService from "../collision/collision_service";
import { StickerService } from "../sticker/sticker_service";
import { CameraSceneScannerAdapter } from "../sticker/internal/camera_scene_scanner_adapter";
import { SceneScanController } from "../scene/scene_scan_controller";
import ConsoleService from "../console/console_service";
import CameraSelectorService from "../cameraselect/camera_selector_service";
import BrowserInfo from "./browserinfo";
import Video from "../../../scene/echodemo/video/video";
import SoundService from "../sound/sound_service";

type GlobalStage = typeof globalThis & {
  __stage?: Stage;
};

export default class Stage {
  scene_service: SceneService;
  render_service: RenderService;
  shader_service: ShaderService;
  compute_service: ComputeService;
  capture_service: CaptureService;
  input_service:InputService;
  resource_service:ResourceService;
  collision_service:CollisionService;
  sticker_service: StickerService;
  console_service: ConsoleService;
  sound_service:SoundService;
  camera_selector_service: CameraSelectorService;
  canvas: HTMLCanvasElement | null = null;

  device_iphone: boolean;
  device_mobile: boolean;
  footerstrings: string[];
  browser_info: BrowserInfo;

  webgpu_supported: boolean | null = null;
  webgpu_checked = false;

  private animationFrameId: number | null = null;
  private lastFrameTime: number | null = null;

  private constructor() {
     console.log('Stage constructor called');
    // In-app debug console. Construction is cheap and SSR-safe (no browser
    // APIs), so it is owned here alongside the other services and reachable as
    // Stage.instance().console_service.
    this.console_service = new ConsoleService();
    this.shader_service = new ShaderService();
    this.resource_service = new ResourceService();
    this.collision_service = new CollisionService();
    this.render_service = new RenderService(this.shader_service,this.resource_service,this.collision_service);
  
    this.compute_service = new ComputeService();
    // Owns the camera pool that feeds the in-scene sticker scanner. Construction
    // is cheap and SSR-safe (no GPU, no permissions); the camera stream is only
    // opened later in start(), inside a user gesture.
    this.capture_service = new CaptureService();
    // Camera selector state/service: lets the user explicitly override the
    // camera before scanning and persists that choice per-origin. React talks
    // only to this service; it drives CaptureService through a narrow port.
    // localStorage is passed only in the browser (SSR-safe).
    this.camera_selector_service = new CameraSelectorService(
      this.capture_service,
      typeof window !== "undefined" ? window.localStorage : null,
      (message: string) => this.console_service.log(message)
    );
    this.input_service = new InputService();

    const device = this.shader_service.get_device();
    if(device)
    {
       this.resource_service.createContext(device);
    }

    this.sound_service = new SoundService();

    this.scene_service = new SceneService();
    // Production default: the camera scene scanner. Scanning is driven through
    // the in-scene Video scanner via an injected controller (no IndexedDB, no
    // backend of its own — session/save state is browser-session only). The
    // controller only sends scene messages, so nothing here touches the camera
    // at construction and this stays SSR-safe. Owners swap in a private cloud
    // adapter here; gameplay code is unchanged.
    const scan_controller = new SceneScanController({
      activate: () => this.scene_service.ui_message("scan_start", ""),
      deactivate: () => this.scene_service.ui_message("scan_stop", ""),
    });
    // StickerService owns the single in-scene camera scanner. We inject the
    // Video factory here (the browser-only side that may reference Video's full
    // GPU graph); the factory runs inside the StickerService constructor, so
    // exactly one Video is created and owned by the service. Video's own
    // constructor is Stage-free, so this does not re-enter Stage construction.
    this.sticker_service = new StickerService({
      adapter: new CameraSceneScannerAdapter({ controller: scan_controller }),
      scannerFactory: () => new Video("scanner-video"),
    });


    this.device_iphone = /iPhone/i.test(navigator.userAgent);
    this.footerstrings = ["echo-engine", "", "", ""];
    this.device_mobile = this.isMobileDevice();
    this.browser_info = new BrowserInfo();
  }

  start() 
  {
    if (this.animationFrameId !== null) return;
    this.lastFrameTime = null;
    this.animationFrameId = requestAnimationFrame(this.frame);
    if(this.render_service.canvas)
    {
        this.input_service.attach(this.render_service.canvas);
    }

  }

  stop()
   {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.lastFrameTime = null;
    this.input_service.detach();
  }

  async detect_webgpu_support(): Promise<boolean> {
    if (this.webgpu_checked && this.webgpu_supported !== null) {
      return this.webgpu_supported;
    }

    if (typeof navigator === 'undefined' || !navigator.gpu) {
      this.webgpu_supported = false;
      this.webgpu_checked = true;
      return false;
    }

    try {
      const adapter = await navigator.gpu.requestAdapter();
      this.webgpu_supported = !!adapter;
      this.webgpu_checked = true;
      return this.webgpu_supported;
    } catch {
      this.webgpu_supported = false;
      this.webgpu_checked = true;
      return false;
    }
  }

  async init_services() {
    const supported = await this.detect_webgpu_support();

    if (!supported) {
      return false;
    }

    await this.shader_service.init();
    await this.compute_service.init();
    // Enumerate cameras up front (no permission prompt, no stream). Guarded so a
    // camera-less environment still brings the engine up; a live scan there
    // surfaces as a typed error when start() fails.
    try {
      await this.capture_service.init();
    } catch (err) {
      console.warn('[stage] capture_service.init failed', err);
    }
    return true;
  }

  isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false;

    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    return isMobileUA || hasTouch;
  }

  get_browser_info() {
    return this.browser_info.get_info(this.webgpu_supported ?? false);
  }

  get_browser_advice() {
    return this.browser_info.get_advice(this.webgpu_supported ?? false);
  }

  footer(text: string, index: number) {
    const el = document.getElementById('app-footer') as HTMLDivElement | null;
    if (el && index < this.footerstrings.length) {
      this.footerstrings[index] = text;
      let msg = "";
      for (const s of this.footerstrings) {
        if (s.length > 0) {
          if (msg.length > 0) msg += " - ";
          msg += s;
        }
      }
      el.textContent = msg;
    }
  }


  private frame = (time: number) => {
    const delta_time =
      this.lastFrameTime === null ? 0 : (time - this.lastFrameTime) / 1000.0;

    this.lastFrameTime = time;
    
    this.scene_service.tick(delta_time);
    this.sound_service.tick(delta_time);
    this.input_service.end_frame();
    this.input_service.begin_frame();

    const device = this.shader_service.get_device();
    if (device)
    {
       this.scene_service.prepare(device);
    }
    this.render_service.render(time);

    this.animationFrameId = requestAnimationFrame(this.frame);
  };


  static instance(): Stage {
    const g = globalThis as GlobalStage;
    if (!g.__stage) {
      g.__stage = new Stage();
    }
    return g.__stage;
  }

}
